import { Component, OnInit, ViewChild } from '@angular/core';

import { translate, translateObject } from '@jsverse/transloco';
import moment from 'moment';

import {
  CallbackEvent,
  DocumentListComponent,
} from 'app/modules/collection/components/document/document-list/document-list.component';
import { QuickActionsItem } from 'app/modules/collection/services/documents-quick-action.service';
import {
  AnalyticItemFilterSets,
  FilterService,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { DashboardViewComponent } from 'app/modules/dashboard/pages/dashboard-view/dashboard-view.component';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { PptxReport } from 'app/modules/widget/services/pptx.service';
import { LexDatePipe } from 'app/pipes/lex-date.pipe';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetDataSource } from '../../../../models';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface WidgetDocumentListOptions {
  collectionName: string;
  filters?: AnalyticItemFilterSets;
  dataSource: WidgetDataSource;
  dataSourceId: string;
  displayColumns: string[];
  pageSize?: number;
  userQuery?: string;
  isClickOpenDocument?: boolean;
  columnDisplayLabels?: { [key: string]: string };
  useFieldNames: { [key: string]: boolean };
  documentViewer: {
    showNlpData?: boolean;
    nlpFields?: { id; display }[];
    showMetadata?: boolean;
    metadataFields?: { id; display }[];
    showQuickActions?: boolean;
    quickActions?: QuickActionsItem[];
  };
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-document-list',
  templateUrl: './widget-document-list.component.html',
  styleUrls: ['./widget-document-list.component.scss'],
  standalone: false,
})
export class WidgetDocumentListComponent
  extends AbstractWidgetComponent<WidgetDocumentListOptions>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('elementData') private elementData: DocumentListComponent;

  downloadFormats = ['excel', 'csv', 'pptx'];
  canFilter = true;
  hasAnalyses = true;
  calculatedQuery = '';
  isCustomDocumentList = false;

  constructor(
    private lexDatePipe: LexDatePipe,
    private dashboardViewComponent: DashboardViewComponent,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();
    this.setWidgetGroup(this.dashboardViewComponent.getWidgetWidgetGroup(this.widget.id));
    this.calculatedQuery = this.query;
    this.setIsCustomDocumentList();
  }

  get defaultTitle(): string {
    return translate('widgets.document-list');
  }

  public async downloadAsExcel() {
    this.elementData?.downloadAsExcel(this.filteredDocumentCount);
  }

  public async exportAsCsv() {
    return this.elementData?.downloadAsCsv();
  }

  async refresh(reset = true, initialLoading = false, refreshedDatasource?: WidgetDataSource) {
    if (!this.dataSourceNotFound || !this.widgetDataSource) {
      this.setWidgetGroup(this.dashboardViewComponent.getWidgetWidgetGroup(this.widget.id));
      this.calculatedQuery = this.query;
      this.setIsCustomDocumentList();
      if (!this.hasInvalidComparison) {
        await this.elementData?.refresh(reset, this.query, initialLoading, refreshedDatasource);
      }
    }
    this.isLoading = false;
  }

  public redraw(): void {
    this.elementData?.setDisplayColumns();
  }

  public get query(): string {
    if (this.dataSourceNotFound || !this.widgetDataSource) {
      return '';
    }
    const widgetQueryOptions = this.widgetQueryOptions;

    if (this.widgetGroup?.isImpact) {
      const previousRange = this.filterService.createDaterangeFilterFromPeriod(
        this.widgetGroup?.parameters?.previousStart,
        this.widgetGroup?.parameters?.previousEnd
      );
      widgetQueryOptions.filters.dates.or.push(previousRange);
      const currentRange = this.filterService.createDaterangeFilterFromPeriod(
        this.widgetGroup?.parameters?.currentStart,
        this.widgetGroup?.parameters?.currentEnd
      );
      widgetQueryOptions.filters.dates.or.push(currentRange);
    }

    const queryForDocuments = this.filterService.getQueryForDocuments(
      widgetQueryOptions,
      false,
      true
    );

    let customItemsQuery;

    // Transform a custom chart to document list
    // Have to add the custom item(s) as document-level filter(s)
    if (this.isCustomDocumentList) {
      const customWidgetQueryOptions = this.widgetQueryOptions;

      customWidgetQueryOptions.filters = this.createCustomItemsFilter();

      customItemsQuery = this.filterService.getQueryForDocuments(
        customWidgetQueryOptions,
        false,
        true
      );
    }

    let query = queryForDocuments;
    if (customItemsQuery) {
      query += ` AND ${customItemsQuery}`;
    }

    return query;
  }

  setIsCustomDocumentList() {
    this.isCustomDocumentList =
      this.widget.properties.as_document_list &&
      (this.widget.payload as any).selectedItems.length > 0;
  }

  private createCustomItemsFilter(): AnalyticItemFilterSets {
    const payload = this.widget.payload as unknown as WidgetChartOptions;
    const defaultFilters = FilterService.getDefaultFilterSet();

    let filterSet = payload.analyticItem;
    let fieldSuffix = 'title';
    if (filterSet.includes('themes')) {
      fieldSuffix = 'normalizedTitle';
    } else if (filterSet.includes('taxonomy')) {
      fieldSuffix = 'taxonomyFullPath';
    }
    let analyticItem = `analyses.${filterSet}.${fieldSuffix}`;

    if (payload.customFieldSelected) {
      analyticItem = `metadata.${filterSet}`;
      filterSet = 'metadata';
    } else {
      if (payload.standardFieldSelected) {
        analyticItem = filterSet;
      }

      const filterConfig = this.filterService.getFilterConfigItem(analyticItem);
      if (filterConfig) {
        filterSet = filterConfig.filterSet as any;
      }
    }

    // currently, a custom chart can only be created for a single entity subtype
    // despite the fact that we support multiple subtypes for non-custom charts
    // That's why it is sufficient to only take the first element of subValue list.
    let subValue = [];
    if (analyticItem.includes('entities') && payload.entityTypeFilter) {
      subValue = payload.entityTypeFilter.split(',');
    }

    defaultFilters[filterSet].or = payload.selectedItems.map((item) => ({
      analyticItem,
      value: item,
      subLabel: subValue[0],
    }));

    return defaultFilters;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    const widgetTitle =
      !this.widget.title && this.widget.title !== '' ? this.widget.title : this.defaultTitle;
    const from = this.elementData.pageIndex * this.elementData.limit + 1;
    const to = from + Math.min(this.elementData.limit, this.elementData.dataSource.page.count) - 1;
    const translations = translateObject('widgets.pptx', {
      'results-from-of-total': {
        title: widgetTitle,
        from,
        to,
        total: this.elementData.dataSource.page.count,
      },
    });

    const otherColumns = this.elementData.otherFields.filter((column) => column.display);

    let colW: any[] = otherColumns.map(() => 1);

    const hasTextColumn = this.elementData.textFields.find(
      (column) => column.display && ['title', 'text', 'abstract'].includes(column.property)
    );

    if (hasTextColumn) {
      // Some basic logic to make the text column wider in case of few columns,
      // and narrower in case of many columns
      colW.unshift(colW.length - (colW.length - 5) / 2);
    }

    const tableWidth = 12; // in inches

    const widthSum = colW.reduce((prev, curr) => prev + curr, 0);
    colW = colW.map((w) => ((w / widthSum) * tableWidth).toFixed(2));
    const rows = [
      [{ text: translations['widget-name'] }, { text: widgetTitle }],
      [{ text: translations['dashboard-name'] }, { text: this.dashboard.title }],
      [{ text: translations['datasource-name'] }, { text: this.widgetDataSource.displayName }],
      [{ text: translations['analysis-id'] }, { text: this.widgetDataSource.analysisId }],
      [{ text: translations['group-id'] }, { text: this.widgetDataSource.groupId }],
      [{ text: translations['generated'] }, { text: moment().format('LLL') }],
    ];

    const dataOptions = this.dataSlideOptions();
    pptx.addTableSlide(
      rows,
      {
        data: dataOptions,
        autoPageLineWeight: -0.3,
        w: tableWidth,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      },
      true
    );

    const pptTable = {
      title: translations['results-from-of-total'],
      rows: [
        otherColumns.map((column) => ({
          text: column.label,
          options: { bold: true, fontSize: 16 },
        })),
      ],
    };

    if (hasTextColumn) {
      pptTable.rows[0].unshift({
        text: translations['text'],
        options: { bold: true, fontSize: 16 },
      });
    }

    this.elementData.dataSource.page.items.forEach((row) => {
      const rowGroup = [];

      let documentText = '';
      if (
        this.elementData.textFields.find((column) => column.display && column.property === 'title')
      ) {
        documentText += translate('widgets.pptx.row-title', {
          title: row.title || translations['no-title'],
        });
      }

      if (
        this.elementData.textFields.find((column) => column.display && column.property === 'text')
      ) {
        const content = this.elementData.isTableViewToggled ? row.text : row.summary || row.text;

        documentText += `\n${translate('widgets.pptx.row-text', { text: content })}`;
      }

      if (
        this.elementData.textFields.find(
          (column) => column.display && column.property === 'abstract'
        )
      ) {
        documentText += `\n${translate('widgets.pptx.row-text', { abstract: row.abstract })}`;
      }

      if (documentText.length) {
        rowGroup.push({ text: documentText });
      }

      otherColumns.forEach((column) => {
        let text = row[column.property];
        if (column.meta) {
          text = row.metadata[column.property];
        }

        if (column.type === 'sentiment') {
          if (this.dashboard.payload.sentimentDisplay === 'polarity') {
            switch (row.analyses[0][`sentimentPolarity${this.dashboard.payload.sentimentScale}`]) {
              case 'very positive':
              case 'positive':
                text = '😃';
                break;
              case 'somewhat positive':
                text = '😊';
                break;
              case 'neutral':
                text = '😐';
                break;
              case 'somewhat negative':
                text = '😞';
                break;
              case 'very negative':
              case 'negative':
                text = '😫';
                break;
              default:
                text = '😵';
            }
          } else {
            text = row.analyses[0].sentimentScore.toFixed(1);
          }
        } else if (column.type === 'date') {
          text = this.lexDatePipe.transform(row[column.property]);
        }

        if (Array.isArray(text)) {
          text = text.join(', ');
        }

        text = text || '';

        rowGroup.push({ text });
      });

      pptTable.rows.push(rowGroup);
    });

    pptx.addTableSlide(pptTable, {
      newSlideStartY: 0.5,
      colW,
      w: tableWidth,
      h: 5.5,
      data: dataOptions,
      autoPage: true,
      lineWeight: -0.3,
      border: {
        color: 'black',
        pt: 2,
        type: 'solid',
      },
    });
  }

  private dataSlideOptions() {
    const dataOptions = {};
    dataOptions['dataSource'] = this.widgetDataSource.displayName;
    dataOptions['docCount'] = this.filteredDocumentCount ?? this.widgetDataSource.documentCount;
    dataOptions['maxDocs'] = this.widgetDataSource.documentCount;
    return dataOptions;
  }

  public canShowDataTable() {
    return false;
  }

  public get textFields() {
    return this.elementData.textFields;
  }

  public get otherFields() {
    return this.elementData.otherFields;
  }

  public setDisplayColumns(textFields, otherFields) {
    this.elementData.setDisplayColumns(textFields, otherFields);
  }

  public async reloadDisplayColumns(widgetDataSource) {
    return this.elementData.configureAvailableFields(widgetDataSource);
  }

  onRefreshDashboard() {
    this.dashboardViewComponent.reload();
  }

  onLoadingStarted($event) {
    this.loadingStarted.emit($event);
  }

  onLoadingFinished($event) {
    this.loadingFinished.emit($event);
  }

  onRecalculateQuery($event: CallbackEvent) {
    this.calculatedQuery = this.query;
    if (this.elementData) {
      this.elementData.options.query = this.calculatedQuery;
    }
    $event.callbackFn();
  }

  get widgetQueryOptions(): WidgetQueryOptions {
    return this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[0]
    );
  }
}
